/**
 * 신뢰도 점수 표시 컴포넌트
 * 
 * 사용자의 신뢰도 점수와 관련 정보를 시각적으로 표시합니다.
 * 
 * @author KUNKONG
 * @created 2026-01-20
 */

import React, { useState, useEffect, useCallback } from 'react';
import { trustScoreAPI, trustUtils } from '../utils/trustAPI';
import { categoryLabel } from '../i18n/categoryNames';
import { useTranslation } from 'react-i18next';
import './TrustScoreDisplay.css';

interface TrustScoreData {
  basic: {
    userId: number;
    name: string;
    nickname: string;
    profileImage?: string;
    currentRating: number;
  };
  trustScore: {
    total: number;
    level: string;
    badge: string;
    lastUpdated: string;
    components?: {
      profile: number;
      project: number;
      rating: number;
      reliability: number;
      activity: number;
    };
  };
  statistics: {
    completedProjects: number;
    totalEarnings?: number;
    repeatClientRate: number;
    onTimeDeliveryRate: number;
  };
  recentProjects: Array<{
    title: string;
    category: string;
    amount?: number;
    rating: number;
    completedAt: string;
    onTime: boolean;
  }>;
  verifiedSkills: Array<{
    skill_name: string;
    proficiency_level: string;
    verification_status: string;
    project_count: number;
    average_rating: number;
  }>;
  categoryExpertise: Array<{
    category_name: string;
    project_count: number;
    avg_rating: number;
    total_earnings: number;
  }>;
}

interface TrustScoreDisplayProps {
  userId: number;
  compact?: boolean; // 간단 버전 표시 여부
  showDetails?: boolean; // 상세 정보 표시 여부
}

const TrustScoreDisplay: React.FC<TrustScoreDisplayProps> = ({
  userId,
  compact = false,
  showDetails = false
}) => {
  const { t } = useTranslation();
  const [trustData, setTrustData] = useState<TrustScoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  const loadTrustScore = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trustScoreAPI.getUserScore(userId);
      if (response.success) {
        setTrustData(response.data);
      } else {
        setError(t('trust.loadFailed'));
      }
    } catch (err: any) {
      console.error('신뢰도 점수 조회 오류:', err);
      setError(err.response?.data?.message || t('common.serverError'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    loadTrustScore();
  }, [userId, loadTrustScore]);

  const handleRecalculate = async () => {
    try {
      setIsRecalculating(true);
      const response = await trustScoreAPI.recalculateScore(userId);
      
      if (response.success) {
        await loadTrustScore(); // 업데이트된 점수 다시 로드
        alert(response.message);
      } else {
        alert(t('trust.recalcFailed'));
      }
    } catch (err: any) {
      console.error('신뢰도 재계산 오류:', err);
      alert(err.response?.data?.message || t('trust.recalcError'));
    } finally {
      setIsRecalculating(false);
    }
  };

  if (loading) {
    return (
      <div className="trust-score-loading">
        <div className="loading-spinner"></div>
        <p>{t('trust.loading')}</p>
      </div>
    );
  }

  if (error || !trustData) {
    return (
      <div className="trust-score-error">
        <p>❌ {error}</p>
        <button onClick={loadTrustScore} className="btn-retry">
          {t('trust.retry')}
        </button>
      </div>
    );
  }

  const { basic, trustScore, statistics, recentProjects, verifiedSkills, categoryExpertise } = trustData;

  // trustScore가 없거나 total이 없는 경우 방어
  if (!trustScore || trustScore.total === undefined) {
    return (
      <div className="trust-score-error">
        <p>{t('trust.cantDisplay')}</p>
        <button onClick={loadTrustScore} className="btn-retry">{t('trust.retry')}</button>
      </div>
    );
  }

  const scoreColor = trustUtils.getScoreColor(trustScore.total);
  const levelDescription = trustUtils.getLevelDescription(trustScore.level);

  // 간단 버전 (프로필 카드용)
  if (compact) {
    return (
      <div className="trust-score-compact">
        <div className="trust-badge" style={{ backgroundColor: scoreColor }}>
          <span className="badge-emoji">{trustScore.badge}</span>
          <span className="badge-score">{t('trust.pointsN', { n: Math.round(trustScore.total) })}</span>
        </div>
        <div className="trust-level">
          <span className="level-text">{trustScore.level}</span>
          <span className="level-description">{levelDescription}</span>
        </div>
      </div>
    );
  }

  // 상세 버전
  return (
    <div className="trust-score-display">
      {/* 메인 점수 표시 */}
      <div className="trust-header">
        <div className="user-info">
          {basic.profileImage && (
            <img src={basic.profileImage} alt={t('profile.imageAlt')} className="profile-image" />
          )}
          <div className="user-details">
            <h3 className="user-name">{basic.name} ({basic.nickname})</h3>
            <div className="current-rating">
              {trustUtils.formatRating(basic.currentRating)}
            </div>
          </div>
        </div>

        <div className="trust-main-score">
          <div className="score-circle" style={{ borderColor: scoreColor }}>
            <span className="score-badge">{trustScore.badge}</span>
            <span className="score-number">{Math.round(trustScore.total)}</span>
            <span className="score-label">{t('trust.pointUnit')}</span>
          </div>
          <div className="trust-level-info">
            <h4 className="level-name">{trustScore.level}</h4>
            <p className="level-desc">{levelDescription}</p>
            <span className="last-updated">
              {t('trust.lastUpdated')}: {trustUtils.formatRelativeTime(trustScore.lastUpdated)}
            </span>
          </div>
        </div>

        {showDetails && (
          <div className="trust-actions">
            <button 
              onClick={handleRecalculate} 
              disabled={isRecalculating}
              className="btn-recalculate"
            >
              {isRecalculating ? t('trust.calculating') : '🔄 ' + t('trust.recalc')}
            </button>
          </div>
        )}
      </div>

      {/* 통계 정보 */}
      <div className="trust-statistics">
        <div className="stat-item">
          <span className="stat-icon">📊</span>
          <span className="stat-label">{t('trust.completedProjects')}</span>
          <span className="stat-value">{t('transactions.countN', { n: statistics.completedProjects })}</span>
        </div>
        <div className="stat-item">
          <span className="stat-icon">🔄</span>
          <span className="stat-label">{t('trust.repeatRate')}</span>
          <span className="stat-value">{Number(statistics.repeatClientRate || 0).toFixed(1)}%</span>
        </div>
        <div className="stat-item">
          <span className="stat-icon">⏰</span>
          <span className="stat-label">{t('trust.onTimeRate')}</span>
          <span className="stat-value">{Number(statistics.onTimeDeliveryRate || 0).toFixed(1)}%</span>
        </div>
        {statistics.totalEarnings && (
          <div className="stat-item">
            <span className="stat-icon">💰</span>
            <span className="stat-label">{t('dashboard.totalEarnings')}</span>
            <span className="stat-value">{trustUtils.formatAmount(statistics.totalEarnings)}</span>
          </div>
        )}
      </div>

      {/* 상세 정보 (본인만 볼 수 있음) */}
      {showDetails && trustScore.components && (
        <div className="trust-breakdown">
          <h5>📈 {t('trust.breakdown')}</h5>
          <div className="score-components">
            <div className="component-item">
              <span className="component-label">{t('trust.cProfile')}</span>
              <div className="component-bar">
                <div 
                  className="component-fill" 
                  style={{ width: `${(trustScore.components.profile / 20) * 100}%` }}
                ></div>
              </div>
              <span className="component-score">{trustScore.components.profile}/20</span>
            </div>
            <div className="component-item">
              <span className="component-label">{t('trust.cProject')}</span>
              <div className="component-bar">
                <div 
                  className="component-fill" 
                  style={{ width: `${(trustScore.components.project / 30) * 100}%` }}
                ></div>
              </div>
              <span className="component-score">{trustScore.components.project}/30</span>
            </div>
            <div className="component-item">
              <span className="component-label">{t('trust.cRating')}</span>
              <div className="component-bar">
                <div 
                  className="component-fill" 
                  style={{ width: `${(trustScore.components.rating / 25) * 100}%` }}
                ></div>
              </div>
              <span className="component-score">{trustScore.components.rating}/25</span>
            </div>
            <div className="component-item">
              <span className="component-label">{t('trust.cReliability')}</span>
              <div className="component-bar">
                <div 
                  className="component-fill" 
                  style={{ width: `${(trustScore.components.reliability / 15) * 100}%` }}
                ></div>
              </div>
              <span className="component-score">{trustScore.components.reliability}/15</span>
            </div>
            <div className="component-item">
              <span className="component-label">{t('trust.cActivity')}</span>
              <div className="component-bar">
                <div 
                  className="component-fill" 
                  style={{ width: `${(trustScore.components.activity / 10) * 100}%` }}
                ></div>
              </div>
              <span className="component-score">{trustScore.components.activity}/10</span>
            </div>
          </div>
        </div>
      )}

      {/* 검증된 스킬 */}
      {verifiedSkills.length > 0 && (
        <div className="verified-skills">
          <h5>✅ {t('trust.verifiedSkills')}</h5>
          <div className="skills-list">
            {verifiedSkills.map((skill, index) => (
              <div key={index} className="skill-item">
                <span className="skill-name">{skill.skill_name}</span>
                <span className="skill-level">{skill.proficiency_level}</span>
                <span className="skill-projects">{t('transactions.countN', { n: skill.project_count })}</span>
                <span className="skill-rating">
                  {trustUtils.formatRating(skill.average_rating, false)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 카테고리별 전문성 */}
      {categoryExpertise.length > 0 && (
        <div className="category-expertise">
          <h5>🎯 {t('trust.expertise')}</h5>
          <div className="expertise-list">
            {categoryExpertise.slice(0, 3).map((category, index) => (
              <div key={index} className="expertise-item">
                <span className="category-name">{categoryLabel(category.category_name)}</span>
                <span className="category-projects">{t('transactions.countN', { n: category.project_count })}</span>
                <span className="category-rating">
                  {trustUtils.formatRating(category.avg_rating, false)}
                </span>
                {category.total_earnings > 0 && (
                  <span className="category-earnings">
                    {trustUtils.formatAmount(category.total_earnings)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 최근 프로젝트 */}
      {recentProjects.length > 0 && (
        <div className="recent-projects">
          <h5>📋 {t('trust.recentProjects')}</h5>
          <div className="projects-list">
            {recentProjects.slice(0, 3).map((project, index) => (
              <div key={index} className="project-item">
                <div className="project-info">
                  <span className="project-title">{project.title}</span>
                  <span className="project-category">{categoryLabel(project.category)}</span>
                  {project.amount && (
                    <span className="project-amount">
                      {trustUtils.formatAmount(project.amount)}
                    </span>
                  )}
                </div>
                <div className="project-metrics">
                  <span className="project-rating">
                    {trustUtils.formatRating(project.rating, false)}
                  </span>
                  <span className={`project-ontime ${project.onTime ? 'on-time' : 'late'}`}>
                    {project.onTime ? '⏰ ' + t('trust.onTime') : '⚠️ ' + t('trust.late')}
                  </span>
                  <span className="project-date">
                    {trustUtils.formatRelativeTime(project.completedAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustScoreDisplay;